import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router";
import {
  getLookupTable,
  getLookupTableEntries,
  updateLookupTable,
  deleteLookupTable,
  renameColumn,
  createEntry,
  updateEntry,
  deleteEntry,
  type LookupTableDetail,
  type LookupEntry,
} from "./api/client";
import { ApiError } from "@/lib/api-client";
import { ColumnEditor } from "./components/ColumnEditor";
import { CsvImportDialog } from "./components/CsvImportDialog";
import { formatDate } from "./utils";
import "../admin.css";
import "./LookupTableDetailPage.css";

// ---------- Delete Table Confirm Dialog ----------

function DeleteTableDialog({
  tableName,
  onConfirm,
  onCancel,
}: {
  tableName: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="lt-dialog-backdrop" onClick={onCancel}>
      <div className="lt-dialog" onClick={(e) => e.stopPropagation()}>
        <h2 className="lt-dialog-title">Delete Lookup Table</h2>
        <p className="lt-dialog-body">
          Are you sure you want to delete <strong>{tableName}</strong>? This will permanently
          delete all columns and entries. This cannot be undone.
        </p>
        <div className="lt-dialog-footer">
          <button type="button" className="rb-btn rb-btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="rb-btn rb-btn-danger" onClick={onConfirm}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Entry Form Dialog (add & edit unified) ----------

function EntryFormDialog({
  title,
  initialKey,
  initialData,
  columns,
  onClose,
  onSave,
}: {
  title: string;
  initialKey: string;
  initialData: Record<string, string>;
  columns: string[];
  onClose: () => void;
  onSave: (key: string, data: Record<string, string>) => Promise<void>;
}) {
  const [keyValue, setKeyValue] = useState(initialKey);
  const [data, setData] = useState<Record<string, string>>(() => ({ ...initialData }));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedKey = keyValue.trim();
    if (!trimmedKey) {
      setError("Key is required.");
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await onSave(trimmedKey, data);
      onClose();
    } catch (err) {
      setError((err as Error).message ?? "Failed to save entry.");
      setIsSubmitting(false);
    }
  }

  return (
    <div className="lt-dialog-backdrop" onClick={onClose}>
      <div className="lt-dialog" onClick={(e) => e.stopPropagation()}>
        <h2 className="lt-dialog-title">{title}</h2>
        <form onSubmit={(e) => void handleSubmit(e)}>
          <div className="lt-dialog-field">
            <label className="lt-dialog-label" htmlFor="entry-key">
              {columns[0] ?? "Key"}
            </label>
            <input
              id="entry-key"
              className="lt-dialog-input"
              type="text"
              value={keyValue}
              onChange={(e) => setKeyValue(e.target.value)}
              autoFocus
              required
            />
          </div>
          {columns.slice(1).map((col) => (
            <div key={col} className="lt-dialog-field">
              <label className="lt-dialog-label" htmlFor={`entry-col-${col}`}>
                {col}
              </label>
              <input
                id={`entry-col-${col}`}
                className="lt-dialog-input"
                type="text"
                value={data[col] ?? ""}
                onChange={(e) => setData((prev) => ({ ...prev, [col]: e.target.value }))}
              />
            </div>
          ))}
          {error && <div className="lt-dialog-error">{error}</div>}
          <div className="lt-dialog-footer">
            <button
              type="button"
              className="rb-btn rb-btn-cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button type="submit" className="rb-btn rb-btn-save" disabled={isSubmitting}>
              {isSubmitting ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------- Entries Table ----------

function EntriesTable({
  entries,
  columns,
  onEdit,
  onDelete,
}: {
  entries: LookupEntry[];
  columns: string[];
  onEdit: (entry: LookupEntry) => void;
  onDelete: (entryId: string) => void;
}) {
  const dataColumns = columns.slice(1);
  return (
    <div className="lt-entries-table-wrap">
      <table className="lt-entries-table">
        <thead>
          <tr>
            <th className="lt-th">{columns[0] ?? "Key"}</th>
            {dataColumns.map((col) => (
              <th key={col} className="lt-th">
                {col}
              </th>
            ))}
            <th className="lt-th lt-th-actions" />
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id} className="lt-tr">
              <td className="lt-td lt-td-key">{entry.key}</td>
              {dataColumns.map((col) => (
                <td key={col} className="lt-td">
                  {entry.data[col] ?? ""}
                </td>
              ))}
              <td className="lt-td lt-td-actions">
                <button
                  type="button"
                  className="lt-row-action"
                  onClick={() => onEdit(entry)}
                  aria-label={`Edit ${entry.key}`}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="lt-row-action lt-row-action--danger"
                  onClick={() => onDelete(entry.id)}
                  aria-label={`Delete ${entry.key}`}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
          {entries.length === 0 && (
            <tr>
              <td className="lt-td lt-entries-empty" colSpan={columns.length + 2}>
                No entries yet. Add a row or import a CSV.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Page ----------

const PAGE_SIZE = 100;

export function LookupTableDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [table, setTable] = useState<LookupTableDetail | null>(null);
  const [entries, setEntries] = useState<LookupEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showDeleteTable, setShowDeleteTable] = useState(false);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [editEntry, setEditEntry] = useState<LookupEntry | null>(null);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [isSavingColumns, setIsSavingColumns] = useState(false);
  const [columnRenameError, setColumnRenameError] = useState<string | null>(null);

  const loadEntries = useCallback(
    async (currentOffset: number) => {
      if (!id) return;
      const page = await getLookupTableEntries(id, { limit: PAGE_SIZE, offset: currentOffset });
      setEntries(page.entries);
      setTotal(page.total);
    },
    [id],
  );

  useEffect(() => {
    if (!id) {
      setError("No lookup table ID provided.");
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    Promise.all([getLookupTable(id), getLookupTableEntries(id, { limit: PAGE_SIZE, offset: 0 })])
      .then(([tableData, entriesPage]) => {
        if (cancelled) return;
        setTable(tableData);
        setEntries(entriesPage.entries);
        setTotal(entriesPage.total);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Failed to load lookup table.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function handleUpdateColumns(columns: string[]) {
    if (!table) return;
    setIsSavingColumns(true);
    try {
      const result = await updateLookupTable(table.id, { columns });
      setTable(result);
    } finally {
      setIsSavingColumns(false);
    }
  }

  async function handleRenameColumn(oldName: string, newName: string) {
    if (!table) return;
    setIsSavingColumns(true);
    setColumnRenameError(null);
    try {
      const result = await renameColumn(table.id, oldName, newName);
      setTable(result);
    } catch (err) {
      // Surface 409 (duplicate) inline next to the column list
      if (err instanceof ApiError && err.status === 409) {
        setColumnRenameError(
          `"${newName}" already exists — choose a different name.`,
        );
      } else {
        setColumnRenameError(
          err instanceof Error ? err.message : "Failed to rename column.",
        );
      }
    } finally {
      setIsSavingColumns(false);
    }
  }

  async function handleAddEntry(key: string, data: Record<string, string>) {
    if (!table) return;
    const entry = await createEntry(table.id, { key, data });
    setEntries((prev) => [...prev, entry]);
    setTotal((t) => t + 1);
    setTable((prev) => (prev ? { ...prev, entryCount: prev.entryCount + 1 } : prev));
  }

  async function handleEditEntry(key: string, data: Record<string, string>) {
    if (!table || !editEntry) return;
    const updated = await updateEntry(table.id, editEntry.id, { key, data });
    setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
  }

  async function handleDeleteEntry(entryId: string) {
    if (!table) return;
    await deleteEntry(table.id, entryId);
    setEntries((prev) => prev.filter((e) => e.id !== entryId));
    setTotal((t) => t - 1);
    setTable((prev) => (prev ? { ...prev, entryCount: prev.entryCount - 1 } : prev));
  }

  async function handleDeleteTable() {
    if (!table) return;
    await deleteLookupTable(table.id);
    navigate("/admin/lookup-tables");
  }

  async function handlePageChange(newOffset: number) {
    setOffset(newOffset);
    await loadEntries(newOffset);
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  if (isLoading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  if (error || !table) {
    return (
      <div className="admin-page">
        <div className="admin-error">{error ?? "Lookup table not found."}</div>
      </div>
    );
  }

  const emptyData = Object.fromEntries(table.columns.map((c) => [c, ""]));

  return (
    <div className="admin-page">
      <div className="admin-header lt-detail-header">
        <div className="lt-detail-breadcrumb">
          <Link to="/admin/lookup-tables" className="lt-back-link">
            &larr; Lookup Tables
          </Link>
        </div>
        <div className="lt-detail-title-row">
          <h1 className="admin-title">{table.name}</h1>
          <button
            type="button"
            className="rb-btn rb-btn-danger"
            onClick={() => setShowDeleteTable(true)}
          >
            Delete Table
          </button>
        </div>
        <div className="lt-detail-meta">
          <span>{table.columns.length} columns</span>
          <span>&middot;</span>
          <span>{table.entryCount} entries</span>
          <span>&middot;</span>
          <span>Updated {formatDate(table.updatedAt)}</span>
        </div>
      </div>

      <div className="lt-detail-section">
        <ColumnEditor
          columns={table.columns}
          onAddColumn={(name) => {
            if (!table.columns.includes(name)) {
              void handleUpdateColumns([...table.columns, name]);
            }
          }}
          onRemoveColumn={(name) => {
            void handleUpdateColumns(table.columns.filter((c) => c !== name));
          }}
          onRenameColumn={(oldName, newName) => {
            // R-76 — use the dedicated rename endpoint so the server
            // validates uniqueness (409) and migrates entry data keys
            // atomically in a single transaction.
            void handleRenameColumn(oldName, newName);
          }}
        />
        {isSavingColumns && <span className="lt-saving-indicator">Saving…</span>}
        {columnRenameError && (
          <span className="lt-column-rename-error" role="alert">
            {columnRenameError}
          </span>
        )}
      </div>

      <div className="lt-detail-section">
        <div className="lt-entries-header">
          <h3 className="lt-section-title">Entries</h3>
          <div className="lt-entries-actions">
            <button
              type="button"
              className="rb-btn rb-btn-cancel"
              onClick={() => setShowCsvImport(true)}
            >
              Import CSV
            </button>
            <button
              type="button"
              className="rb-btn rb-btn-save"
              onClick={() => setShowAddEntry(true)}
            >
              + Add Row
            </button>
          </div>
        </div>

        <EntriesTable
          entries={entries}
          columns={table.columns}
          onEdit={setEditEntry}
          onDelete={(entryId) => void handleDeleteEntry(entryId)}
        />

        {totalPages > 1 && (
          <div className="lt-pagination">
            <button
              type="button"
              className="rb-btn rb-btn-cancel"
              disabled={offset === 0}
              onClick={() => void handlePageChange(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </button>
            <span className="lt-pagination-info">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              className="rb-btn rb-btn-cancel"
              disabled={currentPage >= totalPages}
              onClick={() => void handlePageChange(offset + PAGE_SIZE)}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {showDeleteTable && (
        <DeleteTableDialog
          tableName={table.name}
          onConfirm={() => void handleDeleteTable()}
          onCancel={() => setShowDeleteTable(false)}
        />
      )}

      {showAddEntry && (
        <EntryFormDialog
          title="Add Entry"
          initialKey=""
          initialData={emptyData}
          columns={table.columns}
          onClose={() => setShowAddEntry(false)}
          onSave={handleAddEntry}
        />
      )}

      {editEntry && (
        <EntryFormDialog
          title="Edit Entry"
          initialKey={editEntry.key}
          initialData={{ ...editEntry.data }}
          columns={table.columns}
          onClose={() => setEditEntry(null)}
          onSave={handleEditEntry}
        />
      )}

      {showCsvImport && (
        <CsvImportDialog
          tableId={table.id}
          onClose={() => setShowCsvImport(false)}
          onImported={() => {
            setShowCsvImport(false);
            void loadEntries(offset);
            getLookupTable(table.id).then(setTable).catch(() => {});
          }}
        />
      )}
    </div>
  );
}
